import { useMemo } from "react"
import SimpleBar from "simplebar-react"
import { Link } from "react-router-dom"
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material"
import { useTranslation } from "providers/i18n"
import { CustomIcon, ShopIcon } from "components/icons"
import { Image } from "components/Image"
import { QRCode } from "components/QRCode"
import { ShopListItem as OccupantListItem } from "components/domains/directory"
import { IOccupantDetail } from "utils/feature"

interface IFeatureDetailKioskHProps {
  id: string
  name: string
  list: IOccupantDetail[]
  description: string
  category: string
  categoryIcon: string
  placeDetailMobileUrl: string
  isShowPromotionButton: boolean
  coverImage?: string
  logo?: string
  venueLogo?: string
  levelName?: string
  hours?: string
  onNavigate: () => void
}

export const FeatureDetailKioskH = ({
  id,
  name,
  list: dataList,
  description,
  category,
  categoryIcon,
  placeDetailMobileUrl,
  isShowPromotionButton,
  coverImage,
  logo,
  venueLogo,
  levelName,
  hours,
  onNavigate,
}: IFeatureDetailKioskHProps) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const styleOverrides = theme.components[`AppDetailPage`]?.styleOverrides || {}
  const {
    root,
    mediaCover,
    titleContainer,
    titleActionContainer,
    titleAction,
    headerContainer,
    contentContainer,
    descriptionContainer,
    descriptionText,
    locationContainer,
    locationDetail,
    actionContainer,
    buildingLogo,
    titleText,
    captionText,
    stretchButton,
    avatarRoot,
    list,
    listItem,
    occupantListDetail,
    occupantListContent,
    occupantLisHeader,
  } = styleOverrides

  const isSection = useMemo(() => dataList.length > 0, [dataList])

  return (
    <>
      {isSection && (
        <Card sx={occupantListDetail}>
          <SimpleBar
            className="SimpleBar-container"
            style={{
              width: "100%",
              height: 914,
            }}
          >
            <CardContent sx={occupantListContent}>
              <Typography variant="h5" sx={occupantLisHeader}>
                {t("Brands", { name: t(name) })}
              </Typography>
              <List sx={list}>
                {dataList.map((occupant) => (
                  <Box
                    key={occupant.id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <OccupantListItem
                      sx={listItem}
                      className="FeatureListDetail-listItem"
                      occupant={occupant}
                    />
                  </Box>
                ))}
              </List>
            </CardContent>
          </SimpleBar>
        </Card>
      )}
      <Box sx={root}>
        <CardMedia
          sx={mediaCover}
          className={`${!coverImage ? "no-filter" : ""}`}
          image={coverImage}
          title={t(name)}
        />
        <CardContent sx={contentContainer}>
          <Box sx={headerContainer}>
            <Avatar src={logo} sx={avatarRoot} variant="rounded">
              {categoryIcon ? (
                <CustomIcon path={categoryIcon} fontSize="inherit" />
              ) : (
                <ShopIcon fontSize="inherit" />
              )}
            </Avatar>
            <Box sx={titleContainer}>
              <Typography variant="h5" sx={titleText}>
                {t(name)}
              </Typography>
              <Typography variant="h6" sx={captionText}>
                {t(category)}
              </Typography>
            </Box>
            <Box sx={titleActionContainer}>
              {isShowPromotionButton && (
                <Button
                  component={Link}
                  to={`/promotions/occupant/${id}`}
                  variant="outlined"
                  size="small"
                >
                  <Typography variant="h5" sx={titleAction}>
                    {t("Promotions")}
                  </Typography>
                </Button>
              )}
            </Box>
          </Box>
          <Box sx={descriptionContainer}>
            <Typography sx={descriptionText} variant="body1">
              {t(description)}
            </Typography>
          </Box>
          <Box sx={locationContainer}>
            <Box>
              <QRCode
                url={placeDetailMobileUrl}
                caption={t("Send to Mobile")}
              />
            </Box>
            <List disablePadding sx={locationDetail}>
              <ListItem>
                <ListItemText primary={t("Location")} secondary={levelName} />
                <Box sx={buildingLogo}>
                  {venueLogo && <Image src={venueLogo} />}
                </Box>
              </ListItem>
              {!isSection && (
                <ListItem>
                  <ListItemText
                    primary={t("Operation Time")}
                    secondary={hours || "-"}
                  />
                </ListItem>
              )}
            </List>
          </Box>
          <Box sx={actionContainer}>
            <Button
              sx={stretchButton}
              onClick={onNavigate}
              variant="contained"
              size="medium"
            >
              <Typography variant="h5" sx={titleAction}>
                {t("Directions")}
              </Typography>
            </Button>
          </Box>
        </CardContent>
      </Box>
    </>
  )
}
